import { Button, Modal } from '@insify/design-system/components';
import { anchorColor, headingColor, headingSm, textColor, textMd } from '@insify/design-system/typography';
import { useTranslationsFile } from '@insify/ui-translations';
import { interpolate } from '@insify/ui-utils';
import { TrackedButton } from '@objectiv/tracker-react';
import clsx from 'clsx';

import { ActivitiesList, ListWrapper } from '../ActivitiesList';
import { Details } from '../Details';
import { actions, useActivity } from '../useActivity';

import * as styles from './index.module.css';

import type { Props } from '../ActivitySelector';

export const VerticalModal = ({
  translations,
  showInclusionsExclusions = true,
}: {
  translations: Props['translations'];
  showInclusionsExclusions?: boolean;
}) => {
  const { state, dispatch } = useActivity();
  const isOpen = state.verticalModalId !== undefined;

  if (state.verticalModalId === undefined) {
    return null;
  }

  const { activityIds } = state.verticalById[state.verticalModalId];

  const search = () => {
    dispatch(actions.verticalClosed());
    dispatch(actions.searchFieldFocused());
  };

  const copies = useTranslationsFile<{ verticalModalHeading: string }>({
    translations: {
      verticalModalHeading: interpolate(translations.verticalModalHeading, { name: state.verticalModalId }),
    },
    sendErrorToSentry: true,
    replacements: {
      Search: (props) => (
        <TrackedButton type='button' className={clsx(textMd, anchorColor, styles.search)} onClick={search} {...props} />
      ),
    },
  });

  return (
    <Modal
      open={isOpen}
      onClose={() => {
        dispatch(actions.detailsClosed());
        dispatch(actions.verticalClosed());
      }}
      trackingId={state.verticalModalId}
    >
      {({ forward, backward }) => [
        <div key='vertical'>
          <p className={clsx(headingSm, headingColor)}>{state.verticalModalId}</p>
          <p className={clsx(textMd, textColor)}>{copies.verticalModalHeading}</p>
          <ListWrapper>
            <ActivitiesList
              // We show the same activity in the selected list and in the modal.
              // That can lead to multiple <input/>s with the same name.
              // To avoid that we add a prefix to the id.
              idPrefix={'vertical-modal-'}
              items={activityIds}
              translations={translations}
              onSelect={(id) => dispatch(actions.activitySelectedInVertical(id))}
              onDeselect={(id) => dispatch(actions.activityDeselectedInVertical(id))}
              showInclusionsExclusions={showInclusionsExclusions}
              onDetailsClick={(id) => {
                dispatch(actions.detailsOpened(id));
                forward();
              }}
            />
          </ListWrapper>
          <div className={styles.buttonsBar}>
            <Button
              kind='secondary'
              type='button'
              trackingId='search'
              className={clsx(styles.button, styles.searchButton)}
              onClick={search}
            >
              {translations.searchActivities}
            </Button>
            <Button
              kind='primary'
              type='button'
              trackingId='save'
              className={styles.button}
              onClick={() => dispatch(actions.verticalClosed())}
              data-testid='submit-activities-from-modal'
            >
              {translations.save}
            </Button>
          </div>
        </div>,
        <div key='details'>
          <Details translations={translations} onSelect={backward} />
        </div>,
      ]}
    </Modal>
  );
};
